import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui';
import { useSession } from '../../lib/session';
import { daysSince } from '../../lib/periods';
import { KpiStrip, type Kpi } from './primitives';
import { HoldQueueWidget } from './widgets/HoldQueueWidget';
import { RecentDecisionsWidget } from './widgets/RecentDecisionsWidget';
import { decisionsRecordedQuery, onHoldQuery } from './queries';

/**
 * Quality control. Holds are cleared in place: the decision is made from this
 * queue, because material sitting on hold is stock nobody can use and sending a
 * reviewer to another screen only costs time.
 */
export function QualityDashboard() {
  const { can, shows } = useSession();
  const canRelease = can('qc.release');

  const hold = useQuery(onHoldQuery);
  const recorded = useQuery(decisionsRecordedQuery);

  const lots = hold.data?.items ?? [];
  const oldestDays = lots.reduce((oldest, lot) => Math.max(oldest, daysSince(lot.onHoldSince)), 0);
  const recoveredOnHold = lots.filter((lot) => lot.origin === 'recovered').length;

  const kpis: Kpi[] = [
    {
      label: 'Lots on hold',
      value: hold.isSuccess ? String(lots.length) : '—',
      tone: lots.length > 0 ? 'warn' : 'good',
      hint: 'waiting for a decision',
    },
    {
      label: 'Oldest hold',
      value: lots.length === 0 ? '—' : `${oldestDays}d`,
      tone: oldestDays >= 7 ? 'bad' : oldestDays > 0 ? 'warn' : 'default',
      hint: 'age of the oldest lot',
    },
    {
      label: 'Recovered on hold',
      value: hold.isSuccess ? String(recoveredOnHold) : '—',
      hint: 'reclaimed material blocked',
    },
    {
      label: 'Decisions recorded',
      value: recorded.data ? String(recorded.data.total) : '—',
      hint: 'all time',
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Quality"
        title="Holds waiting on you"
        description="Material on hold is counted and valued but cannot be drawn into production. Releasing makes a lot usable; rejecting is a one-way door that has to be reopened deliberately."
      />

      <KpiStrip items={kpis} />

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <HoldQueueWidget
          limit={8}
          canDecide={canRelease}
          to={shows('quality') ? '/quality' : undefined}
        />
        <RecentDecisionsWidget to={shows('quality') ? '/quality' : undefined} />
      </div>
    </>
  );
}
